import { useState } from 'react';
import { DOCUMENTOS } from '@aponte/shared';
import { api, type DocumentoNaFicha } from '../../api/cliente.js';
import { mensagemDoErro } from '../../componentes/Aviso.js';

const QUANDO = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'America/Sao_Paulo',
});

interface Props {
  candidatoId: string;
  documentos: DocumentoNaFicha[];
}

/**
 * Documentos emitidos, com link de abertura sob demanda.
 *
 * A URL do PDF é assinada e vale 15 minutos, então **não pode ser guardada na tela**: um
 * link renderizado junto com a página morreria enquanto a pessoa lê. Cada clique pede uma
 * URL nova à API — o custo é uma requisição, e a troca é não haver link eterno para
 * documento contratual circulando em histórico de navegador.
 */
export function ListaDeDocumentos({ candidatoId, documentos }: Props) {
  const [abrindo, setAbrindo] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  if (documentos.length === 0) {
    return (
      <div className="aguardando">
        Nenhum documento emitido ainda. A Carta de Intenções é o primeiro passo da esteira.
      </div>
    );
  }

  async function abrir(documento: DocumentoNaFicha) {
    setAbrindo(documento.documentoId);
    setErro(null);
    try {
      const { url } = await api.urlDoDocumento(candidatoId, documento.documentoId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setErro(mensagemDoErro(e));
    } finally {
      setAbrindo(null);
    }
  }

  const ordenados = [...documentos].sort((a, b) => b.emitidoEm.localeCompare(a.emitidoEm));

  return (
    <>
      <table className="funil">
        <thead>
          <tr>
            <th>Documento</th>
            <th>Emitido em</th>
            <th>Assinatura</th>
            <th>Identificador</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {ordenados.map((d) => (
            <tr key={d.documentoId}>
              <td className="empresa">
                {DOCUMENTOS[d.tipo].nome}
                <small>
                  versão {d.versao} · {(d.tamanhoBytes / 1024).toFixed(0)} KB
                  {d.desatualizado && (
                    <>
                      {' · '}
                      <span className="aviso-desatualizado">
                        ⚠ a ficha mudou depois desta emissão
                      </span>
                    </>
                  )}
                </small>
              </td>
              <td>
                {QUANDO.format(new Date(d.emitidoEm))}
                <br />
                <small style={{ color: 'var(--apagado)' }}>{d.emitidoPor}</small>
              </td>
              <td>
                {d.assinadoRecebidoEm ? (
                  <span className="selo positivo">
                    Assinado{d.chaveS3Assinado ? ' · com arquivo' : ''}
                  </span>
                ) : (
                  <span className="selo neutro">Só emitido</span>
                )}
              </td>
              <td>
                <code style={{ fontSize: 10, color: 'var(--apagado)' }}>
                  {d.hash.slice(0, 12)}…
                </code>
              </td>
              <td style={{ textAlign: 'right' }}>
                <button
                  className="bt vazado pequeno"
                  disabled={abrindo === d.documentoId}
                  onClick={() => void abrir(d)}
                >
                  {abrindo === d.documentoId ? 'Abrindo…' : 'Abrir PDF'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {erro && (
        <div className="aviso erro" style={{ marginTop: 12 }}>
          {erro}
        </div>
      )}

      <div className="dica" style={{ marginTop: 12 }}>
        O link de cada PDF vale 15 minutos e é gerado a cada clique. O identificador é o início do
        SHA-256 do arquivo, o mesmo impresso no rodapé do documento — serve para conferir que o PDF
        em mãos é o que foi emitido aqui.
      </div>
    </>
  );
}
